import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useState } from "react";

/* ================= COMPONENTS ================= */
import Header from "./components/Header";
import Hero from "./components/Hero";
import HomeInfo from "./components/HomeInfo";
import Solutions from "./components/Solutions";
import CoreStrengths from "./components/CoreStrengths";
import Why from "./components/Why";
import Process from "./components/Process";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";

/* ================= PAGES ================= */
import About from "./components/About";
import Contact from "./components/Contact";
import Projects from "./components/Projects";
import LoginPage from "./components/LoginPage";
import SignupPage from "./components/SignupPage";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import Dashboard from "./components/Dashboard";
import AdminDashboard from "./components/AdminDashboard";
import EmployeeDashboard from "./components/EmployeeDashboard";
import AddEmployee from "./components/AddEmployee";
import AddProject from "./components/AddProject";
import ProtectedRoute from "./components/ProtectedRoute";

/* ================= SOLUTIONS ================= */
import SolarEPC from "./components/SolarEPC";
import Installation from "./components/Installation";
import Maintenance from "./components/Maintenance";
import FreeSiteSurvey from "./components/FreeSiteSurvey";

/* ================= CAREERS ================= */
import OpenJobs from "./components/careers/OpenJobs";
import Internships from "./components/careers/Internships";

/* ================= LAYOUTS ================= */
function MainLayout({ children }) {
  return (
    <div className="app-container min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">{children}</main>
      <Footer />
    </div>
  );
}

function AuthLayout({ children }) {
  return <div className="min-h-screen">{children}</div>;
}

/* ================= REDIRECT WRAPPER ================= */
function RedirectOnLogin({ user }) {
  if (!user) return null;
  if (user.role === "admin") return <Navigate to="/admin-dashboard" />;
  if (user.role === "employee") return <Navigate to="/employee-dashboard" />;
  return <Navigate to="/dashboard" />;
}

/* ================= APP ================= */
export default function App() {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("user");
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem("user");
  };

  return (
    <Router>
      <ScrollToTop />

      <Routes>
        {/* ================= LOGIN / SIGNUP ================= */}
        <Route
          path="/login"
          element={
            user ? (
              <RedirectOnLogin user={user} />
            ) : (
              <AuthLayout>
                <LoginPage onLoginSuccess={handleLoginSuccess} />
              </AuthLayout>
            )
          }
        />

        <Route
          path="/signup"
          element={
            user ? (
              <RedirectOnLogin user={user} />
            ) : (
              <AuthLayout>
                <SignupPage />
              </AuthLayout>
            )
          }
        />

        <Route path="/forgot-password" element={<AuthLayout><ForgotPassword /></AuthLayout>} />
        <Route path="/reset-password" element={<AuthLayout><ResetPassword /></AuthLayout>} />

        {/* ================= DASHBOARDS ================= */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute user={user} requiredRole="user">
              <Dashboard user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard"
          element={
            <ProtectedRoute user={user} requiredRole="admin">
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/employee-dashboard"
          element={
            <ProtectedRoute user={user} requiredRole="employee">
              <EmployeeDashboard user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />

        {/* ================= ADMIN ACTIONS ================= */}
        <Route
          path="/add-employee"
          element={
            <ProtectedRoute user={user} requiredRole="admin">
              <AddEmployee />
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-project"
          element={
            <ProtectedRoute user={user} requiredRole="admin">
              <AddProject />
            </ProtectedRoute>
          }
        />

        {/* ================= MAIN WEBSITE ================= */}
        <Route
          path="/"
          element={
            <MainLayout>
              <Hero />
              <HomeInfo />
              <Solutions />
              <CoreStrengths />
              <Why />
              <Process />
            </MainLayout>
          }
        />

        <Route path="/about" element={<MainLayout><About /></MainLayout>} />
        <Route path="/projects" element={<MainLayout><Projects /></MainLayout>} />
        <Route path="/contact" element={<MainLayout><Contact /></MainLayout>} />

        {/* ================= SOLUTIONS ================= */}
        <Route path="/solutions/solar-epc" element={<MainLayout><SolarEPC /></MainLayout>} />
        <Route path="/solutions/installation" element={<MainLayout><Installation /></MainLayout>} />
        <Route path="/solutions/maintenance" element={<MainLayout><Maintenance /></MainLayout>} />
        <Route path="/free-site-survey" element={<MainLayout><FreeSiteSurvey /></MainLayout>} />

        {/* ================= CAREERS ================= */}
        <Route path="/careers/jobs" element={<MainLayout><OpenJobs /></MainLayout>} />
        <Route path="/careers/internships" element={<MainLayout><Internships /></MainLayout>} />

        {/* ================= FALLBACK ================= */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}